import React, { memo } from 'react'
import DropTrailerIcon from 'transfix-icons/build/DropTrailerIcon'
import AppointmentTimeRange from 'transfix-ui/components/AppointmentTag/components/AppointmentTimeRange'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import Skeleton from 'transfix-ui/core/Skeleton'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { IShipmentStop, IShipmentStopAppointment, Maybe } from 'types/graphqlTypes'
import { capitalize } from 'utils/capitalize'
import StopsDivider, { IStopsDividerProps } from '../StopsDivider'

const useShipmentStopsPreviewStyles = makeStyles(theme => ({
  flex: {
    display: 'flex',
    alignItems: 'center',
  },
  divider: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: `0 ${theme.spacing(1.5)}`,
  },
  stop: {
    '& .appointment': {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  },
}))

interface IShipmentStopDetailsProps {
  stop: Pick<IShipmentStop, 'city' | 'stateCode' | 'dropTrailer'> & {
    appointment: Pick<
      IShipmentStopAppointment,
      'endAt' | 'startAt' | 'timezone' | 'startAtHasTime' | 'endAtHasTime'
    >
  }
}

export const ShipmentStopDetails = memo<IShipmentStopDetailsProps>(({ stop }) => {
  const { appointment, city, dropTrailer, stateCode } = stop || {}
  const { endAt, endAtHasTime, startAt, startAtHasTime, timezone } = appointment || {}
  return (
    <div>
      <Typography variant='subtitle2' component='div'>
        {capitalize(city || '')}, {stateCode}
      </Typography>
      <Typography variant='caption' color='textSecondary' component='div' className='appointment'>
        <AppointmentTimeRange
          start={{ datetime: startAt, timezone, hasTime: startAtHasTime }}
          end={{ datetime: endAt, timezone, hasTime: endAtHasTime }}
        />
        {dropTrailer && <Tooltip trigger={<DropTrailerIcon size='medium' />}>Drop Trailer</Tooltip>}
      </Typography>
    </div>
  )
})

const ShipmentStopDetailsSkeleton = memo(() => {
  return (
    <div>
      <Skeleton variant='text' width={80} height={19} />
      <Skeleton variant='text' width={125} height={16} />
    </div>
  )
})

export interface IShipmentStopsPreviewProps {
  /**
   * @default false
   */
  loading?: boolean
  firstPickup: Maybe<IShipmentStopDetailsProps['stop']>
  lastDelivery: Maybe<IShipmentStopDetailsProps['stop']>
  stops: IStopsDividerProps['stops']
}

const ShipmentStopsPreview = memo<IShipmentStopsPreviewProps>(
  ({ firstPickup, lastDelivery, loading = false, stops }) => {
    const classes = useShipmentStopsPreviewStyles()

    return (
      <div className={classes.flex}>
        <div className={classes.stop}>
          {loading && <ShipmentStopDetailsSkeleton />}
          {!loading && firstPickup && <ShipmentStopDetails stop={firstPickup} />}
        </div>
        <div className={classes.divider}>
          <StopsDivider stops={stops} />
        </div>
        <div className={classes.stop}>
          {loading && <ShipmentStopDetailsSkeleton />}
          {!loading && lastDelivery && <ShipmentStopDetails stop={lastDelivery} />}
        </div>
      </div>
    )
  }
)

export default ShipmentStopsPreview
